import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, IconButton, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Paper, Typography
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
    adminFetchTemplatesThunk,
    adminCreateTemplateThunk,
    adminUpdateTemplateThunk,
    adminDeleteTemplateThunk,
    selectAllAdminTemplates,
    selectAdminTemplatesApi,
    selectAdminTemplateCreateApi,
    selectAdminTemplateUpdateApi,
    selectAdminTemplateDeleteApi,
    resetAdminTemplateMutationApis,
} from 'store/reducers/temple/whatsappSlice';
import { WhatsAppTemplateType, AdminTemplateRequest } from 'api/whatsapp/types';
import { apiStatus } from 'shared/constants/AppEnum';
import { useSnackbar } from 'notistack';
import TemplateFormDialog from './TemplateFormDialog';
import HeadingCard from 'components/Card/HeadingCard';

const AdminTemplateTable: React.FC = () => {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const templates = useAppSelector(selectAllAdminTemplates);
    const listApi = useAppSelector(selectAdminTemplatesApi);
    const createApi = useAppSelector(selectAdminTemplateCreateApi);
    const updateApi = useAppSelector(selectAdminTemplateUpdateApi);
    const deleteApi = useAppSelector(selectAdminTemplateDeleteApi);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTpl, setEditingTpl] = useState<(WhatsAppTemplateType & { id: number }) | null>(null);

    useEffect(() => {
        dispatch(adminFetchTemplatesThunk(undefined));
    }, [dispatch]);

    // Handle create/update/delete success
    useEffect(() => {
        if (createApi.status === apiStatus.SUCCEEDED) {
            enqueueSnackbar('Template created successfully', { variant: 'success' });
            setDialogOpen(false);
            dispatch(resetAdminTemplateMutationApis());
        }
        if (updateApi.status === apiStatus.SUCCEEDED) {
            enqueueSnackbar('Template updated successfully', { variant: 'success' });
            setDialogOpen(false);
            setEditingTpl(null);
            dispatch(resetAdminTemplateMutationApis());
        }
        if (deleteApi.status === apiStatus.SUCCEEDED) {
            enqueueSnackbar('Template deleted successfully', { variant: 'success' });
            dispatch(resetAdminTemplateMutationApis());
        }
    }, [createApi.status, updateApi.status, deleteApi.status, enqueueSnackbar, dispatch]);

    const handleCreate = () => {
        setEditingTpl(null);
        setDialogOpen(true);
    };

    const handleEdit = (tpl: WhatsAppTemplateType) => {
        setEditingTpl(tpl as WhatsAppTemplateType & { id: number });
        setDialogOpen(true);
    };

    const handleDelete = (id: number) => {
        if (window.confirm('Are you sure you want to delete this template?')) {
            dispatch(adminDeleteTemplateThunk(id));
        }
    };

    const handleSubmit = (data: AdminTemplateRequest) => {
        if (editingTpl) {
            dispatch(adminUpdateTemplateThunk({ id: editingTpl.id, data }));
        } else {
            dispatch(adminCreateTemplateThunk(data));
        }
    };

    const handleClose = () => {
        setDialogOpen(false);
        setEditingTpl(null);
        dispatch(resetAdminTemplateMutationApis());
    };

    return (
        <>
            <HeadingCard title="WhatsApp Templates (Admin)" cardContent={false} hasheaderBackground={false}>
                <Box display="flex" justifyContent="flex-end" p={2}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleCreate}
                    >
                        Add Template
                    </Button>
                </Box>
                {listApi.status === apiStatus.LOADING ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : templates.length === 0 ? (
                    <Box textAlign="center" my={4}>
                        <Typography color="textSecondary">No templates found</Typography>
                    </Box>
                ) : (
                    <TableContainer component={Paper} elevation={0}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Language</TableCell>
                                    <TableCell>Header</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Default</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {templates.map((tpl) => (
                                    <TableRow key={tpl.id}>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                    {tpl.template_name || (tpl as any).templateName}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {tpl.description}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={tpl.category_display || (tpl as any).categoryDisplay || tpl.category}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>{tpl.language}</TableCell>
                                        <TableCell>{tpl.header_type_display || (tpl as any).headerTypeDisplay || tpl.header_type}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={(tpl.is_active ?? (tpl as any).isActive) ? 'Active' : 'Inactive'}
                                                size="small"
                                                color={(tpl.is_active ?? (tpl as any).isActive) ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {(tpl.is_default ?? (tpl as any).isDefault) && (
                                                <Chip label="Default" size="small" color="primary" />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" onClick={() => handleEdit(tpl)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleDelete(tpl.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </HeadingCard>

            <TemplateFormDialog
                open={dialogOpen}
                onClose={handleClose}
                onSubmit={handleSubmit}
                api={editingTpl ? updateApi : createApi}
                initialData={editingTpl ? {
                    template_name: editingTpl.template_name || (editingTpl as any).templateName,
                    category: editingTpl.category,
                    description: editingTpl.description,
                    language: editingTpl.language,
                    header_type: (editingTpl.header_type || (editingTpl as any).headerType) as any,
                    has_buttons: editingTpl.has_buttons ?? (editingTpl as any).hasButtons,
                    body_template: editingTpl.body_template || (editingTpl as any).bodyTemplate,
                    is_active: editingTpl.is_active ?? (editingTpl as any).isActive,
                    is_default: editingTpl.is_default ?? (editingTpl as any).isDefault,
                    notes: editingTpl.notes,
                    id: editingTpl.id,
                } : undefined}
                mode={editingTpl ? 'edit' : 'create'}
            />
        </>
    );
};

export default AdminTemplateTable;
